import { useState, useEffect } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Download, Calendar, Globe, Tag, FileText, Copy, Check, DollarSign, CreditCard, Loader2, BookmarkPlus, BookmarkCheck, Wand2, ArrowLeft, Smartphone, QrCode } from "lucide-react";
import { QrShareDialog } from "../components/QrShareDialog";
import { Input } from "@/components/ui/input";
import Navigation from "../components/Navigation";
import MobileMoneyDialog from "../components/MobileMoneyDialog";
import AskAIPanel from "../components/AskAIPanel";
import DocumentTranslationPanel from "../components/DocumentTranslationPanel";
import InlineNewCollection from "../components/InlineNewCollection";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SimilarItemsSection = ({ items }) => {
  const navigate = useNavigate();
  if (!items || items.length === 0) return null;
  return (
    <div className="p-8 border-t border-slate-100" data-testid="similar-items-section">
      <h2 className="text-xl font-semibold mb-5" style={{ fontFamily: 'Fraunces, serif' }}>Similar Items</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((it) => (
          <div
            key={it.id}
            className="border border-slate-200 rounded-lg p-4 hover:border-orange-300 hover:shadow-sm transition-all cursor-pointer group"
            onClick={() => navigate(`/items/${it.id}`)}
            data-testid={`similar-item-${it.id}`}
          >
            <h3 className="text-sm font-semibold text-slate-900 line-clamp-2 group-hover:text-orange-600 transition-colors" style={{ fontFamily: 'Fraunces, serif' }}>
              {it.title}
            </h3>
            {it.authors?.length > 0 && (
              <p className="text-xs text-slate-500 mt-1 line-clamp-1">{it.authors.join(', ')}</p>
            )}
            {it.abstract && (
              <p className="text-xs text-slate-500 mt-1 line-clamp-2">{it.abstract}</p>
            )}
            <div className="flex flex-wrap gap-1.5 mt-2">
              {it.year && (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-slate-100 text-slate-600 text-[10px] rounded">
                  <Calendar className="w-2.5 h-2.5" />{it.year}
                </span>
              )}
              {it.language && (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-slate-100 text-slate-600 text-[10px] rounded">
                  <Globe className="w-2.5 h-2.5" />{it.language}
                </span>
              )}
              {it.file_type && (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-slate-100 text-slate-600 text-[10px] rounded">
                  <FileText className="w-2.5 h-2.5" />{it.file_type}
                </span>
              )}
              {it.price != null && it.price > 0 && (
                <span className="px-1.5 py-0.5 bg-green-50 text-green-700 text-[10px] rounded font-medium">
                  ${it.price.toFixed(2)}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ItemDetailPage = ({ user }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [purchaseEmail, setPurchaseEmail] = useState(''); // kept for backwards compatibility, not shown when logged in
  const [processingPayment, setProcessingPayment] = useState(false);
  const [hasPurchased, setHasPurchased] = useState(false);
  const [mmOpen, setMmOpen] = useState(false);
  const [downloadToken, setDownloadToken] = useState(null);
  const [pollingStatus, setPollingStatus] = useState(false);
  const [freeUsage, setFreeUsage] = useState({ used: 0, limit: 10 });
  const [membership, setMembership] = useState(null);
  const [thumbnailError, setThumbnailError] = useState(false);
  const [useHiResPreview, setUseHiResPreview] = useState(true);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [abstractLang, setAbstractLang] = useState("original");
  const [translatedAbstracts, setTranslatedAbstracts] = useState({});
  const [similarItems, setSimilarItems] = useState([]);
  const [myCollections, setMyCollections] = useState([]);
  const [showSaveMenu, setShowSaveMenu] = useState(false);
  const [collectionQuery, setCollectionQuery] = useState("");
  const [showQr, setShowQr] = useState(false);
  const [savedIn, setSavedIn] = useState(new Set());
  const [generatingAbstract, setGeneratingAbstract] = useState(false);

  // Determine which page to return to
  const getBackPath = () => {
    if (sessionStorage.getItem("admin_items_state")) return "/admin/items";
    if (sessionStorage.getItem("smart_search_ui_state")) return "/smart-search";
    if (user?.role === "admin") return "/admin/items";
    return "/search";
  };

  // Keep purchase email in sync with logged-in user when available
  useEffect(() => {
    if (user?.email) {
      setPurchaseEmail(user.email);
    }
  }, [user]);

  const [translating, setTranslating] = useState(false);
  // Share preview feature temporarily disabled
  // const [shareLink, setShareLink] = useState("");
  // const [shareLoading, setShareLoading] = useState(false);
  // const isSuperadmin = user?.email?.toLowerCase() === "admin@archive.com";
  // const is2RAAdmin =
  //   user?.role === "admin" && (user?.organization || "").trim().toUpperCase() === "2RA-EDITION";
  // const canShare = (() => {
  //   if (!user) return false;
  //   if (isSuperadmin || is2RAAdmin) return true;
  //   // Only active subscription users can share (no trial/grace/free-access)
  //   return user.subscription_status === "active";
  // })();




  useEffect(() => {
    fetchItem();
    
    // Load current free download usage and membership for banners
    const loadUsageAndMembership = async () => {
      try {
        const token = localStorage.getItem('access_token');
        if (!token) return;
        const headers = { Authorization: `Bearer ${token}` };
        const [usageResp, membershipResp] = await Promise.all([
          axios.get(`${API}/auth/download-usage`, { headers }),
          axios.get(`${API}/auth/membership-status`, { headers }),
        ]);
        if (usageResp.data && typeof usageResp.data.used === 'number') {
          setFreeUsage({ used: usageResp.data.used, limit: usageResp.data.limit ?? 10 });
        }
        if (membershipResp.data) {
          setMembership(membershipResp.data);
        }
      } catch (err) {
        console.error('Usage/membership load error:', err);
      }
    };

    loadUsageAndMembership();
    
    // Check if returning from Stripe payment
    const sessionId = searchParams.get('session_id');
    const paymentStatus = searchParams.get('payment');
    
    if (sessionId && paymentStatus === 'success') {
      pollStripeStatus(sessionId);
    } else if (paymentStatus === 'cancelled') {
      toast.error('Payment was cancelled');
      // Clear URL params
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [id, searchParams]);

  // ----- SEO: inject per-item JSON-LD + update document title/description -----
  useEffect(() => {
    if (!item) return;

    // Prefer the production canonical host so structured data matches the
    // deployed URLs Google actually indexes, regardless of preview host.
    const publicHost = "https://2ra-edition.com";
    const itemUrl = `${publicHost}/items/${item.id}`;
    const isArticle = ["article", "preprint"].includes((item.category || "").toLowerCase());
    const schemaType = isArticle ? "ScholarlyArticle" : "Book";

    const authors = Array.isArray(item.authors) && item.authors.length > 0
      ? item.authors.map((name) => ({ "@type": "Person", "name": name }))
      : undefined;

    const jsonld = {
      "@context": "https://schema.org",
      "@type": schemaType,
      "@id": itemUrl,
      "url": itemUrl,
      "name": item.title,
      "headline": item.title,
      ...(authors ? { "author": authors } : {}),
      ...(item.abstract ? { "abstract": item.abstract, "description": item.abstract } : {}),
      ...(item.year ? { "datePublished": String(item.year) } : {}),
      ...(item.language ? { "inLanguage": item.language } : {}),
      ...(Array.isArray(item.tags) && item.tags.length > 0 ? { "keywords": item.tags.join(", ") } : {}),
      ...(item.page_count ? { "numberOfPages": item.page_count } : {}),
      "isAccessibleForFree": !item.is_paid,
      "encodingFormat": "application/pdf",
      ...(item.has_thumbnail ? { "image": `${BACKEND_URL}/api/items/${item.id}/thumbnail` } : {}),
      "publisher": {
        "@type": "Organization",
        "name": item.organization && item.organization !== "Public" ? item.organization : "2RA-Edition",
        "url": publicHost,
      },
      ...(item.is_paid && item.price
        ? {
            "offers": {
              "@type": "Offer",
              "price": String(item.price),
              "priceCurrency": "USD",
              "availability": "https://schema.org/InStock",
              "url": itemUrl,
            },
          }
        : {}),
    };

    // Inject / replace the <script type="application/ld+json" id="item-jsonld"> tag
    const SCRIPT_ID = "item-jsonld";
    let scriptTag = document.getElementById(SCRIPT_ID);
    if (!scriptTag) {
      scriptTag = document.createElement("script");
      scriptTag.type = "application/ld+json";
      scriptTag.id = SCRIPT_ID;
      document.head.appendChild(scriptTag);
    }
    scriptTag.textContent = JSON.stringify(jsonld);

    // Also update document.title + meta description + canonical for this item
    const cleanTitle = (item.title || "").trim().slice(0, 90);
    const authorList = Array.isArray(item.authors) ? item.authors.slice(0, 3).join(", ") : "";
    document.title = cleanTitle
      ? `${cleanTitle}${authorList ? ` — ${authorList}` : ""} | The Archive`
      : "The Archive";

    const setMetaTag = (attrName, attrValue, contentValue) => {
      let el = document.querySelector(`meta[${attrName}="${attrValue}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attrName, attrValue);
        document.head.appendChild(el);
      }
      el.setAttribute("content", contentValue);
    };

    const descText = (item.abstract || `${cleanTitle}${authorList ? ` by ${authorList}` : ""}. Available at The Archive by 2RA-Edition.`).slice(0, 300);
    setMetaTag("name", "description", descText);
    setMetaTag("property", "og:title", cleanTitle);
    setMetaTag("property", "og:description", descText);
    setMetaTag("property", "og:url", itemUrl);
    setMetaTag("property", "og:type", isArticle ? "article" : "book");
    if (item.has_thumbnail) {
      setMetaTag("property", "og:image", `${BACKEND_URL}/api/items/${item.id}/thumbnail`);
    }

    // Canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", itemUrl);

    // Cleanup when leaving the item detail page — restore site defaults so
    // other pages don't inherit stale per-item metadata.
    return () => {
      const tag = document.getElementById(SCRIPT_ID);
      if (tag) tag.remove();
      document.title = "The Archive";
      setMetaTag(
        "name",
        "description",
        "The Archive by 2RA-Edition — a curated digital library of 8,000+ books, academic articles, research reports, and theses in engineering, extractive metallurgy, mining, chemistry, and scientific research.",
      );
      setMetaTag("property", "og:url", `${publicHost}/`);
      setMetaTag("property", "og:type", "website");
      if (canonical) canonical.setAttribute("href", `${publicHost}/`);
    };
  }, [item]);

  const fetchItem = async () => {
    try {
      const response = await axios.get(`${API}/items/${id}`);
      setItem(response.data);
      // Seed cached translations so they don't trigger a fresh LLM call
      if (response.data?.translations && typeof response.data.translations === "object") {
        setTranslatedAbstracts(response.data.translations);
      }
      setThumbnailError(false);
      setUseHiResPreview(true);
      
      // Check if user has already purchased (by their account email when logged in)
      const emailToCheck = user?.email || localStorage.getItem('purchase_email');
      if (emailToCheck && response.data.is_paid) {
        checkPurchase(emailToCheck);
        setPurchaseEmail(emailToCheck);
      }

      // Fetch similar items
      axios.get(`${API}/items/${id}/similar?limit=6`)
        .then(r => setSimilarItems(r.data?.items || []))
        .catch(() => {});

      // Fetch user's collections for this item
      const tkn = localStorage.getItem("access_token");
      if (tkn) {
        axios.get(`${API}/items/${id}/in-collections`, { headers: { Authorization: `Bearer ${tkn}` } })
          .then(r => {
            setMyCollections(r.data || []);
            setSavedIn(new Set((r.data || []).filter(c => c.contains).map(c => c.id)));
          })
          .catch(() => {});
      }
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Error loading item');
    } finally {
      setLoading(false);
    }
  };

  const checkPurchase = async (email) => {
    try {
      const response = await axios.get(`${API}/payments/verify/${id}?email=${encodeURIComponent(email)}`);
      if (response.data.has_purchased) {
        setHasPurchased(true);
        setDownloadToken(response.data.download_token);
      }
    } catch (error) {
      console.error('Purchase check error:', error);
    }
  };

  const checkPurchaseAndGetToken = async () => {
    try {
      const email = user?.email || localStorage.getItem('purchase_email');
      if (!email) return false;
      const response = await axios.get(`${API}/payments/verify/${id}?email=${encodeURIComponent(email)}`);
      if (response.data.has_purchased && response.data.download_token) {
        setDownloadToken(response.data.download_token);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Purchase verification error:', error);
      return false;
    }
  };

  const getDownloadTokenFromStorage = async () => {
    const email = user?.email || localStorage.getItem('purchase_email');
    if (!email) return null;
    try {
      const response = await axios.get(`${API}/payments/verify/${id}?email=${encodeURIComponent(email)}`);
      if (response.data.has_purchased && response.data.download_token) {
        setDownloadToken(response.data.download_token);
        return response.data.download_token;
      }
    } catch (error) {
      console.error('Token retrieval error:', error);
    }
    return null;
  };

  const pollStripeStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;
    
    if (attempts >= maxAttempts) {
      toast.error('Payment verification timed out. Please refresh the page.');
      setPollingStatus(false);
      return;
    }
    
    setPollingStatus(true);
    
    try {
      const response = await axios.get(`${API}/stripe/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        // Refresh purchase status and get download token
        const verified = await checkPurchaseAndGetToken();
        if (verified) {
          if (verified) {
            toast.success('Payment successful! You can now download the file.');
            setHasPurchased(true);
          } else {
            // Payment recorded but purchase verification failed - retry
            toast.info('Payment recorded. Verifying purchase...');
            setTimeout(() => pollStripeStatus(sessionId, attempts + 1), pollInterval);
            return;
          }
        } else {
          toast.error('Email not found. Please try purchasing again.');
        }
        
        setPollingStatus(false);
        // Clear URL params
        window.history.replaceState({}, '', window.location.pathname);
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        setPollingStatus(false);
        window.history.replaceState({}, '', window.location.pathname);
        return;
      }
      
      // Continue polling
      setTimeout(() => pollStripeStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Status check error:', error);
      setTimeout(() => pollStripeStatus(sessionId, attempts + 1), pollInterval);
    }
  };

  const handlePurchase = async () => {
    // If user is logged in, always use their account email
    const emailToUse = user?.email || purchaseEmail;
    if (!emailToUse || !emailToUse.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setProcessingPayment(true);
    localStorage.setItem('purchase_email', emailToUse);
    
    try {
      const response = await axios.post(`${API}/stripe/create-checkout`, {
        item_id: id,
        email: emailToUse,
        origin_url: window.location.origin
      });
      
      if (response.data.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Failed to create checkout session');
        setProcessingPayment(false);
      }
    } catch (error) {
      console.error('Purchase error:', error);
      toast.error(error.response?.data?.detail || 'Error creating payment');
      setProcessingPayment(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        toast.error('Please login to download this file.');
        return;
      }
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.get(`${API}/items/${id}/download`, {
        responseType: 'blob',
        headers
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', item.title + '.' + (item.file_type || 'pdf').toLowerCase());
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Download started');
    } catch (error) {
      console.error('Download error:', error);
      let status = error.response?.status;
      let detail = null;

      // When responseType is 'blob', error responses are also blobs — parse them
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          detail = parsed.detail;
        } catch (_) { /* not JSON */ }
      } else {
        detail = error.response?.data?.detail;
      }

      if (status === 404) {
        toast.error(detail || 'This item has no file available for download.');
      } else if (status === 403 || status === 401) {
        toast.error(detail || 'Access denied. Please login.');
      } else if (error.code === "ERR_NETWORK" || error.message?.includes("Network Error")) {
        toast.error('Download failed — the file may be too large. Please try again.');
      } else {
        toast.error(detail || 'Error downloading file');
      }
    } finally {
      setDownloading(false);
    }
  };

  const handlePaidDownload = async () => {
    if (!downloadToken) {
      // Try to recover by checking purchase again
      const savedEmail = localStorage.getItem('purchase_email');
      if (savedEmail) {
        const verified = await checkPurchaseAndGetToken(savedEmail);
        if (!verified) {
          toast.error('Purchase not found. Please verify your email or try purchasing again.');
          return;
        }
        // Token was just set, proceed with download
      } else {
        toast.error('No purchase email found. Please enter your email and purchase again.');
        return;
      }
    }
    
    setDownloading(true);
    try {
      const token = downloadToken || (await getDownloadTokenFromStorage());
      if (!token) {
        toast.error('Download token not available. Please refresh the page.');
        setDownloading(false);
        return;
      }
      
      const response = await axios.get(`${API}/items/${id}/paid-download?token=${token}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', item.title + '.' + (item.file_type || 'pdf').toLowerCase());
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Download started');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Error downloading file. Please contact support.');
    } finally {
      setDownloading(false);
    }
  };

  // Share preview feature temporarily disabled
  // const generateShareLink = async () => {
  //   if (!item || !canShare) return;
  //   setShareLoading(true);
  //   try {
  //     const token = localStorage.getItem("access_token");
  //     if (!token) {
  //       toast.error("You must be logged in to share this item.");
  //       setShareLoading(false);
  //       return;
  //     }
  //
  //     const response = await axios.post(
  //       `${API}/items/${id}/share`,
  //       {},
  //       {
  //         headers: { Authorization: `Bearer ${token}` },
  //       }
  //     );
  //     const { share_token } = response.data || {};
  //     if (!share_token) {
  //       toast.error("Failed to generate share link.");
  //       setShareLoading(false);
  //       return;
  //     }
  //
  //     const origin = window.location.origin;
  //     const url = `${origin}/share/${share_token}`;
  //     setShareLink(url);
  //     try {
  //       await navigator.clipboard.writeText(url);
  //       toast.success("Share link copied to clipboard");
  //     } catch {
  //       toast.success("Share link generated. Copy it from the field below.");
  //     }
  //   } catch (error) {
  //     console.error("Share link error:", error);
  //     const msg =
  //       error.response?.data?.detail ||
  //       "You do not have permission to share this item.";
  //     toast.error(msg);
  //   } finally {
  //     setShareLoading(false);
  //   }
  // };

  const generateBibTeX = () => {
    if (!item) return '';
    return `@article{${item.id},
  title={${item.title}},
  author={${item.authors?.join(' and ') || 'Unknown'}},
  year={${item.year || 'n.d.'}},
  language={${item.language}}
}`;
  };

  const generateRIS = () => {
    if (!item) return '';
    return `TY  - JOUR
TI  - ${item.title}
${item.authors?.map(a => `AU  - ${a}`).join('\n') || 'AU  - Unknown'}
PY  - ${item.year || 'n.d.'}
LA  - ${item.language}
ER  -`;
  };

  const copyCitation = (format) => {
    const citation = format === 'bibtex' ? generateBibTeX() : generateRIS();
    navigator.clipboard.writeText(citation);
    setCopied(true);
    toast.success(`${format === 'bibtex' ? 'BibTeX' : 'RIS'} copied to clipboard`);
    setTimeout(() => setCopied(false), 2000);
  };

  const getDisplayAbstract = () => {
    if (!item?.abstract) return "";
    if (abstractLang === "original") return item.abstract;
    return translatedAbstracts[abstractLang] || "";
  };

  const handleGenerateAbstract = async () => {
    if (!item?.id) return;
    setGeneratingAbstract(true);
    try {
      const token = localStorage.getItem("access_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const resp = await axios.post(`${API}/items/${item.id}/generate-abstract`, {}, { headers, timeout: 120000 });
      const { abstract } = resp.data || {};
      if (abstract) {
        setItem((prev) => ({ ...prev, abstract }));
        toast.success("Abstract generated successfully!");
      }
    } catch (error) {
      console.error("Generate abstract error:", error);
      let detail = "Error generating abstract";
      if (error.response?.data?.detail) {
        detail = error.response.data.detail;
      } else if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
        detail = "Request timed out. The file may be too large. Please try again.";
      } else if (error.message?.includes("Network Error")) {
        detail = "Network error. Please check your connection and try again.";
      }
      toast.error(detail);
    } finally {
      setGeneratingAbstract(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation user={user} />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">Loading...</div>
        </div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation user={user} />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">Item not found</div>
        </div>
      </div>
    );
  }

  const price = item.price || 30;

  const showThumbnail = item.has_file && item.file_type === 'PDF' && !thumbnailError;
  const previewImgSrc = useHiResPreview
    ? `${API}/items/${id}/preview-image`
    : `${API}/items/${id}/thumbnail`;
  const handlePreviewImgError = () => {
    if (useHiResPreview) {
      setUseHiResPreview(false);
    } else {
      setThumbnailError(true);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} />

      {/* Fullscreen thumbnail preview dialog */}
      {isPreviewOpen && showThumbnail && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
          onClick={() => setIsPreviewOpen(false)}
        >
          <div
            className="relative max-w-5xl max-h-[90vh] w-full px-4"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setIsPreviewOpen(false)}
              className="absolute -top-10 right-0 text-sm text-white/80 hover:text-white"
            >
              Close [Esc]
            </button>
            <div className="bg-slate-900 rounded-md p-2 flex justify-center items-center">
              <img
                src={previewImgSrc}
                alt={`First page of ${item.title}`}
                className="max-h-[86vh] w-auto object-contain rounded"
                onError={handlePreviewImgError}
              />
            </div>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button
          variant="ghost"
          size="sm"
          className="mb-4 text-slate-600 hover:text-slate-900"
          onClick={() => navigate(getBackPath())}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          {/* Header */}
          <div className="p-8 border-b border-slate-200">
            <div className="flex items-center gap-2 mb-4">
              <span className="uppercase tracking-widest text-xs text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                {item.file_type || 'DOCUMENT'}
              </span>
              {item.is_paid && (
                <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                  ${price.toFixed(2)}
                </span>
              )}
            </div>
            <div className="flex flex-col md:flex-row gap-6">
              {showThumbnail && (
                <div className="w-full md:w-2/5 flex justify-center">
                  <button
                    type="button"
                    onClick={() => setIsPreviewOpen(true)}
                    className="relative group focus:outline-none"
                    aria-label="Open preview in full screen"
                  >
                    <img
                      src={previewImgSrc}
                      alt={`First page of ${item.title}`}
                      className="rounded-md border border-slate-200 shadow-sm max-h-[30rem] object-contain group-hover:brightness-95 group-hover:shadow-md transition"
                      onError={handlePreviewImgError}
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition rounded-md">
                      <span className="text-xs font-medium text-white bg-black/70 px-2 py-1 rounded">Click to enlarge</span>
                    </div>
                  </button>
                </div>
              )}
              <div className="flex-1">
                <h1 className="text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Fraunces, serif' }} data-testid="item-title">
                  {item.title}
                </h1>
            {item.authors && item.authors.length > 0 && (
              <p className="text-lg text-slate-600 mb-4" data-testid="item-authors">
                {item.authors.join(', ')}
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              {item.year && (
                <span className="inline-flex items-center px-3 py-1 bg-slate-100 text-slate-700 text-sm rounded">
                  <Calendar className="w-4 h-4 mr-2" />
                  {item.year}
                </span>
              )}
              {item.language && (
                <span className="inline-flex items-center px-3 py-1 bg-slate-100 text-slate-700 text-sm rounded">
                  <Globe className="w-4 h-4 mr-2" />
                  {item.language}
                </span>
              )}
              {item.tags && item.tags.map((tag, idx) => (
                <span key={idx} className="inline-flex items-center px-3 py-1 bg-orange-50 text-orange-700 text-sm rounded">
                  <Tag className="w-4 h-4 mr-2" />
                  {tag}
                </span>
              ))}
            </div>
              </div>
            </div>
          </div>

          {/* Save to Collection + QR buttons */}
          <div className="px-8 pb-4 -mt-2 relative flex flex-wrap items-center gap-2" data-testid="save-to-collection-area">
            {user && (
            <Button
              variant="outline"
              size="sm"
              className={`text-sm ${savedIn.size > 0 ? "border-orange-300 text-orange-600" : ""}`}
              onClick={() => { setShowSaveMenu(!showSaveMenu); setCollectionQuery(""); }}
              data-testid="save-to-collection-btn"
            >
              {savedIn.size > 0 ? <BookmarkCheck className="w-4 h-4 mr-2" /> : <BookmarkPlus className="w-4 h-4 mr-2" />}
              {savedIn.size > 0 ? `Saved in ${savedIn.size} collection${savedIn.size > 1 ? "s" : ""}` : "Save to Collection"}
            </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              className="text-sm"
              onClick={() => setShowQr(true)}
              data-testid="item-qr-btn"
            >
              <QrCode className="w-4 h-4 mr-2" /> QR code
            </Button>
            <QrShareDialog
              open={showQr}
              onOpenChange={setShowQr}
              url={`${window.location.origin}/items/${id}`}
              title={item.title}
              subtitle="The Archive · Scan to open this document"
            />
            {user && showSaveMenu && (
                <div className="absolute z-10 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg w-72 p-3" data-testid="collection-dropdown">
                  {myCollections.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-2">No collections yet. <a href="/collections" className="text-orange-600 underline">Create one</a></p>
                  ) : (
                    <>
                      {myCollections.length > 3 && (
                        <input
                          type="text"
                          value={collectionQuery}
                          onChange={(e) => setCollectionQuery(e.target.value)}
                          placeholder="Search collections…"
                          autoFocus
                          className="w-full h-8 mb-2 rounded-md border border-slate-200 bg-slate-50 px-2.5 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:bg-white"
                          data-testid="collection-search-input"
                        />
                      )}
                      <div className="space-y-1 max-h-48 overflow-y-auto">
                        {myCollections
                          .filter((c) => !collectionQuery.trim() || c.name.toLowerCase().includes(collectionQuery.trim().toLowerCase()))
                          .map((c) => (
                        <button
                          key={c.id}
                          className={`w-full flex items-center justify-between px-3 py-2 rounded text-sm text-left transition-colors ${savedIn.has(c.id) ? "bg-emerald-100 border border-emerald-300 text-emerald-900 font-medium hover:bg-emerald-200" : "text-slate-700 hover:bg-slate-50 border border-transparent"}`}
                          onClick={async () => {
                            const tkn = localStorage.getItem("access_token");
                            try {
                              if (savedIn.has(c.id)) {
                                await axios.delete(`${API}/collections/${c.id}/items/${id}`, { headers: { Authorization: `Bearer ${tkn}` } });
                                setSavedIn(prev => { const s = new Set(prev); s.delete(c.id); return s; });
                                toast.success(`Removed from "${c.name}"`);
                              } else {
                                await axios.post(`${API}/collections/${c.id}/items/${id}`, {}, { headers: { Authorization: `Bearer ${tkn}` } });
                                setSavedIn(prev => new Set(prev).add(c.id));
                                toast.success(`Added to "${c.name}"`);
                              }
                            } catch (err) { toast.error("Error updating collection"); }
                          }}
                          data-testid={`toggle-collection-${c.id}`}
                        >
                          <span className="truncate">{c.name}</span>
                          {savedIn.has(c.id) && <Check className="w-4 h-4 shrink-0 text-emerald-600" />}
                        </button>
                          ))}
                        {collectionQuery.trim() && myCollections.every((c) => !c.name.toLowerCase().includes(collectionQuery.trim().toLowerCase())) && (
                          <p className="text-xs text-slate-400 text-center py-2" data-testid="collection-search-no-results">No collections match “{collectionQuery.trim()}”.</p>
                        )}
                      </div>
                    </>
                  )}
                  <div className="border-t border-slate-200 mt-2 pt-2">
                    <InlineNewCollection
                      itemId={id}
                      onCreated={(c) => {
                        setMyCollections((prev) => [...prev, c]);
                        setSavedIn((prev) => new Set(prev).add(c.id));
                      }}
                    />
                    <div className="mt-2">
                      <a href="/collections" className="text-xs text-orange-600 hover:underline" data-testid="manage-collections-link">Manage collections</a>
                    </div>
                  </div>
                </div>
            )}
          </div>

          {/* Abstract */}
          {item.abstract ? (
            <div className="p-8 border-b border-slate-200 space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold" style={{ fontFamily: 'Fraunces, serif' }}>Abstract</h2>
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-slate-500">Language:</span>
                  <Select
                    value={abstractLang}
                    onValueChange={async (value) => {
                      setAbstractLang(value);
                      if (value === "original") return;
                      if (translatedAbstracts[value]) return;
                      if (!item?.id) return;
                      try {
                        setTranslating(true);
                        const tkn = localStorage.getItem("access_token");
                        if (!tkn) {
                          toast.error("Please log in to translate abstracts");
                          setAbstractLang("original");
                          setTranslating(false);
                          return;
                        }
                        const resp = await axios.post(
                          `${API}/items/${item.id}/translate-abstract`,
                          { target_language: value },
                          { headers: { Authorization: `Bearer ${tkn}` } },
                        );
                        const translated = resp.data?.abstract || "";
                        setTranslatedAbstracts((prev) => ({ ...prev, [value]: translated }));
                      } catch (err) {
                        console.error("Translate abstract error:", err);
                        toast.error(err.response?.data?.detail || "Error translating abstract");
                        setAbstractLang("original");
                      } finally {
                        setTranslating(false);
                      }
                    }}
                  >
                    <SelectTrigger className="h-7 w-36 text-xs">
                      <SelectValue placeholder="Language" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="original">Original</SelectItem>
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="fr">Français</SelectItem>
                      <SelectItem value="es">Español</SelectItem>
                      <SelectItem value="de">Deutsch</SelectItem>
                      <SelectItem value="it">Italiano</SelectItem>
                      <SelectItem value="pt">Português</SelectItem>
                      <SelectItem value="zh">中文</SelectItem>
                      <SelectItem value="ja">日本語</SelectItem>
                      <SelectItem value="ar">العربية</SelectItem>
                      <SelectItem value="ru">Русский</SelectItem>
                    </SelectContent>
                  </Select>
                  {translating && (
                    <span className="flex items-center gap-1 text-slate-400">
                      <Loader2 className="w-3 h-3 animate-spin" /> Translating…
                    </span>
                  )}
                </div>
              </div>
              <p className="text-slate-700 leading-relaxed" data-testid="item-abstract">{getDisplayAbstract()}</p>
            </div>
          ) : (
            <div className="p-8 border-b border-slate-200 space-y-3">
              <h2 className="text-xl font-semibold" style={{ fontFamily: 'Fraunces, serif' }}>Abstract</h2>
              <p className="text-slate-500 text-sm italic">No abstract available for this document.</p>
              {item.has_file && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={generatingAbstract}
                  onClick={handleGenerateAbstract}
                  data-testid="generate-abstract-btn"
                >
                  {generatingAbstract ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                  ) : (
                    <><Wand2 className="w-4 h-4 mr-2" /> Generate Abstract with AI</>
                  )}
                </Button>
              )}
            </div>
          )}

          {/* Ask AI about this document */}
          {user && item.has_file && <AskAIPanel itemId={item.id} />}

          {/* Share preview section temporarily disabled */}
          {/*
          {typeof canShare !== 'undefined' && canShare && (
            <div className="p-8 border-b border-slate-200">
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between gap-4">
                  <h2
                    className="text-xl font-semibold"
                    style={{ fontFamily: 'Fraunces, serif' }}
                  >
                    Share preview
                  </h2>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={generateShareLink}
                    disabled={shareLoading}
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    {shareLoading ? 'Generating...' : 'Generate link'}
                  </Button>
                </div>
                {shareLink && (
                  <div className="flex flex-col gap-2">
                    <Input
                      type="text"
                      value={shareLink}
                      readOnly
                      className="w-full text-xs"
                    />
                    <p className="text-xs text-slate-500">
                      This link shows a public preview of this item for 24 hours. Paid downloads still require purchase.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
          */}

          {/* Download Section */}
          {item.has_file && (
            <div className="p-8 border-b border-slate-200">
              <div className="flex items-center justify-between mb-4">
                <div className="flex flex-col gap-1">
                  <h2 className="text-xl font-semibold" style={{ fontFamily: 'Fraunces, serif' }}>Download</h2>
                  {membership?.subscription_status === 'active' && (
                    <div className="text-[11px] text-green-700 bg-green-50 border border-green-200 rounded-full inline-flex items-center px-2 py-0.5 mt-1">
                      Active subscription – no free-download limit.
                    </div>
                  )}
                </div>
                {user && !item.is_paid && freeUsage.used > 0 && freeUsage.used < freeUsage.limit && (
                  <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-full px-3 py-1">
                    You have downloaded {freeUsage.used}/{freeUsage.limit} free items this month.
                  </div>
                )}
              </div>
              
              {/* Free Download — also shown to admins on paid items (admin bypass) */}
              {(!item.is_paid || user?.role === 'admin') && (
                <div className="flex flex-col gap-2">
                  <Button
                    onClick={handleDownload}
                    disabled={downloading}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                    data-testid="download-file-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {downloading ? 'Downloading...' : user ? 'Download File' : 'Login to download'}
                  </Button>
                  {item.is_paid && user?.role === 'admin' && (
                    <p className="text-xs text-slate-500 italic">
                      Admin bypass — paid item, free for you.
                    </p>
                  )}
                  {user && !item.is_paid && freeUsage.used >= freeUsage.limit && (
                    <div className="flex items-center gap-3 text-xs text-slate-600 mt-1">
                      <span>You&apos;ve reached your free download limit for this month.</span>
                      <Button
                        type="button"
                        variant="outline"
                        size="xs"
                        onClick={() => window.location.assign('/subscriptions')}
                        className="h-7 px-3 text-xs border-slate-300"
                      >
                        Go to subscriptions
                      </Button>
                    </div>
                  )}
                </div>
              )}
              
              {/* Paid Download — hidden for admins (they get the free button above) */}
              {item.is_paid && user?.role !== 'admin' && (
                <div>
                  {(processingPayment || pollingStatus) && (
                    <div className="flex items-center gap-2 text-slate-600 mb-4">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {pollingStatus ? 'Verifying payment...' : 'Redirecting to payment...'}
                    </div>
                  )}
                  
                  {hasPurchased ? (
                    <div>
                      <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
                        <p className="text-green-700 font-medium">✓ Purchase verified for {user?.email || purchaseEmail}</p>
                      </div>
                      <Button
                        onClick={handlePaidDownload}
                        disabled={downloading}
                        className="bg-orange-500 hover:bg-orange-600 text-white"
                        data-testid="paid-download-btn"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        {downloading ? 'Downloading...' : 'Download File'}
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {!user ? (
                        <div className="space-y-3">
                          <div className="p-4 bg-slate-50 border border-slate-200 rounded-md">
                            <p className="text-slate-700 mb-2">This file requires a one-time payment of <strong>{price.toFixed(2)}</strong></p>
                            <p className="text-sm text-slate-500">
                              Please log in to purchase and download this file. Your account email will be used for access.
                            </p>
                          </div>
                          <Button
                            type="button"
                            onClick={() => window.location.assign('/login')}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                          >
                            Go to login
                          </Button>
                        </div>
                      ) : (
                        <>
                          <div className="p-4 bg-slate-50 border border-slate-200 rounded-md">
                            <p className="text-slate-700 mb-2">This file requires a one-time payment of <strong>{price.toFixed(2)}</strong></p>
                            <p className="text-sm text-slate-500">
                              You will receive download access at your account email: {user.email}
                            </p>
                          </div>
                          
                          <div className="flex gap-3 flex-wrap">
                            <Button
                              onClick={handlePurchase}
                              disabled={processingPayment}
                              className="bg-blue-600 hover:bg-blue-700 text-white"
                              data-testid="buy-now-btn"
                            >
                              <CreditCard className="w-4 h-4 mr-2" />
                              {processingPayment
                                ? 'Processing...'
                                : `Pay $${price.toFixed(2)}`}
                            </Button>
                            <button
                              type="button"
                              onClick={() => setMmOpen(true)}
                              className="text-sm px-4 py-2 rounded-md border border-slate-300 text-slate-800 hover:border-orange-400 hover:text-orange-700 flex items-center gap-2"
                              data-testid="buy-mobile-money-btn"
                            >
                              <Smartphone className="w-4 h-4" />
                              Pay with Mobile Money
                            </button>
                          </div>
                          
                          <p className="text-xs text-slate-500">
                            Secure payment via Stripe or Mobile Money (Airtel, Orange, M-Pesa, MTN).
                          </p>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* AI document translation — attached alternative next to the original file */}
              <DocumentTranslationPanel item={item} user={user} />
            </div>
          )}

          {/* Citations */}
          <div className="p-8">
            <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Fraunces, serif' }}>Cite This Work</h2>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-slate-700">BibTeX</label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyCitation('bibtex')}
                    data-testid="copy-bibtex-btn"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <pre className="bg-slate-50 p-4 rounded text-xs overflow-x-auto" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="bibtex-citation">
                  {generateBibTeX()}
                </pre>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-slate-700">RIS</label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyCitation('ris')}
                    data-testid="copy-ris-btn"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <pre className="bg-slate-50 p-4 rounded text-xs overflow-x-auto" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="ris-citation">
                  {generateRIS()}
                </pre>
              </div>
            </div>
          </div>

          {/* Similar Items */}
          <SimilarItemsSection items={similarItems} />
        </div>
      </div>

      <MobileMoneyDialog
        open={mmOpen}
        onClose={() => setMmOpen(false)}
        onSuccess={() => { setHasPurchased(true); setMmOpen(false); }}
        purpose="item"
        itemId={item?.id}
        itemTitle={item?.title}
      />
    </div>
  );
};

export default ItemDetailPage;
